"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";

// Homepage in every locale: "/" (km, unprefixed) or "/en".
const isHomePath = (path: string) => /^\/(?:en|km)?\/?$/.test(path);

function scrollToSection(hash: string, behavior: ScrollBehavior) {
  const id = decodeURIComponent(hash.replace(/^#/, ""));
  if (!id || id === "top") {
    window.scrollTo({ top: 0, behavior });
    return true;
  }
  const target = document.getElementById(id);
  if (!target) return false;
  target.scrollIntoView({ behavior, block: "start" });
  return true;
}

// Keeps the homepage URL clean: section links (#about, #contact…) scroll to the
// section without leaving a hash in the address bar.
export function HomeHashLinks() {
  const pathname = usePathname();

  useEffect(() => {
    if (!isHomePath(pathname)) return;
    const clean = () =>
      window.history.replaceState(
        window.history.state,
        "",
        window.location.pathname + window.location.search,
      );

    // Arrived from another page via "/#about": scroll once the section exists, then drop the hash.
    let retry: number | undefined;
    const { hash } = window.location;
    if (hash) {
      let attempts = 0;
      const tryScroll = () => {
        if (scrollToSection(hash, "smooth") || ++attempts > 20) clean();
        else retry = window.setTimeout(tryScroll, 100);
      };
      tryScroll();
    }

    const onClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      const anchor = (event.target as Element | null)?.closest?.("a[href]");
      if (!(anchor instanceof HTMLAnchorElement) || anchor.target === "_blank")
        return;
      const url = new URL(anchor.href, window.location.href);
      if (
        !url.hash ||
        url.origin !== window.location.origin ||
        url.pathname !== window.location.pathname
      )
        return;
      if (!scrollToSection(url.hash, "smooth")) return;
      event.preventDefault();
      clean();
    };
    document.addEventListener("click", onClick);
    return () => {
      document.removeEventListener("click", onClick);
      window.clearTimeout(retry);
    };
  }, [pathname]);

  return null;
}
